/** POST .../outcome-estimation/input-uploads の応答。 */
export interface InputUploadDef {
  upload_id: string;
  model_id: string;
  method_type: string;
  /** S3 の署名付き URL。ここへ直接 PUT する。 */
  upload_url: string;
  /** PUT 時にそのまま付けるヘッダ（Content-Type: text/csv）。 */
  required_headers: Record<string, string>;
  /** tasks 作成時にそのまま渡す。 */
  input_file: OutcomeEstimationFileReference;
  /** 署名の有効期限（発行から 1 時間）。 */
  expires_at: string;
}

export interface OutcomeEstimationFileReference {
  storage_type: string;
  saved_path: string;
}

/** GET /xcausal/ipw/smd-balance の 1 行。 */
export interface SmdRow {
  parent_node_id: string;
  parent_node_name: string;
  layer_no: number;
  smd: number;
}

/** POST /xcausal/ipw/compute の応答。 */
export interface IpwComputeDef {
  ipw_run_id: string;
  asset_id: string;
}

/** GET /xcausal/ipw/smd-balance の応答。 */
export interface SmdBalanceDef {
  asset_id: string;
  upload_id: string;
  ipw_run_id?: string | null;
  /** リクエストからは外れたが、応答には引き続き含まれる。 */
  outcome?: string;
  /** 元データ数 */
  existing_row_count: number;
  /** アップロードデータ数 */
  uploaded_row_count: number;
  uploaded_to_existing_ratio: number;
  /** IPW 適用後は true */
  weighted: boolean;
  rows: SmdRow[];
}

/** 画面が使う形へ正規化した SMD。 */
export interface SmdBalance {
  existingRowCount: number;
  uploadedRowCount: number;
  /** IPW 適用後の値なら true */
  weighted: boolean;
  rows: {
    id: string;
    name: string;
    layerNo: number;
    smd: number;
    /** |SMD| が閾値を超えて偏っているか */
    imbalanced: boolean;
  }[];
}

/**
 * SMD の偏り判定の閾値。
 * 一般に 0.1 を超えると無視できない偏りとされる。BE 確認後に調整する。
 */
export const SMD_IMBALANCE_THRESHOLD = 0.1;

export type EstimationStatus = "pending" | "processing" | "finished" | "error";

/** POST .../outcome-estimation/tasks と GET .../tasks/{task_id} の応答。 */
export interface EstimationTaskDef {
  task_id: string;
  status: EstimationStatus;
  method_type?: string;
  /** 完了するまで null。 */
  estimation_id: string | null;
  error?: { code?: string; message?: string } | null;
}

/** GET .../estimations/{estimation_id} の応答。 */
export interface EstimationDef {
  estimation_id: string;
  task_id: string;
  status: EstimationStatus;
  outcome: string;
  /** 相対パス。そのまま叩くとCSVが落ちる。 */
  download_url: string;
  /** アップロードした行数 */
  uploaded_profile_count: number;
  /** 学習に使った元データの行数 */
  fit_source_record_count: number;
  /** 結果の行数（元データ + アップロード分） */
  result_record_count: number;
  gauge_domain?: { min: number; max: number } | null;
}

/** アップロード分だけの行か、元データ由来の行か。 */
export type RecordPresence = "uploaded_only" | "source_only" | string;

/** GET .../estimations/{estimation_id}/records の 1 行。 */
export interface EstimationRecordDef {
  record_id: string;
  identifier_code: string;
  presence: RecordPresence;
  /** `source_only` の行はこちらに実測値が入る（`point_estimate` は null）。 */
  observed_value: number | null;
  /** `uploaded_only` の行はこちらに推定値が入る（`observed_value` は null）。 */
  point_estimate: number | null;
  estimate_statistic?: string;
  uncertainty_interval: {
    type: string;
    level: number;
    method: string;
    lower: number;
    upper: number;
  } | null;
}

export interface EstimationRecordsDef {
  items: EstimationRecordDef[];
}

/** 画面が使う形へ正規化した推定結果の 1 行。 */
export interface EstimationRecord {
  id: string;
  /** 識別コード */
  identifierCode: string;
  presence: RecordPresence;
  /** アップロードした行（＝推定した行）。表で色を変える */
  isUploaded: boolean;
  /**
   * 表に出す値。`uploaded_only` なら推定値、`source_only` なら実測値。
   * どちらも無ければ null。
   */
  value: number | null;
  /** 推定値か実測値か。信頼区間が付くのは推定値のときだけ。 */
  isEstimated: boolean;
  lower: number | null;
  upper: number | null;
}
